import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Chemistry & Molecules — 8 ANIMATED templates
   periodic_table_view, molecule_3d, chemical_bond, ph_scale,
   crystal_lattice, gas_particles, distillation_setup,
   electrochemistry_cell
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Periodic Table View ──────────────────────────────── */
export const PeriodicTableView: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Periodic Table";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const elements = ["H", "He", "Li", "Be", "B", "C", "N", "O", "F", "Ne", "Na", "Mg", "Al", "Si", "P", "S", "Cl", "Ar"];
  const colors = ["#e74c3c", "#9b59b6", "#3498db", "#2ecc71", "#f39c12", "#1abc9c"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {elements.map((el, i) => {
        const col = i % 9, row = Math.floor(i / 9);
        const x = 80 + col * 72, y = 100 + row * 100;
        const c = colors[i % colors.length];
        return (
          <g key={i}>
            <rect x={x} y={y} width="64" height="80" rx="8"
              fill={i === activeIndex ? c : "#1c2833"}
              stroke={i === activeIndex ? "#fff" : c} strokeWidth={i === activeIndex ? 3 : 1.5}
              opacity={i === activeIndex ? 1 : 0.5}>
              {i === activeIndex && (
                <animate attributeName="stroke-width" values="3;5;3" dur="1s" repeatCount="indefinite" />
              )}
            </rect>
            <text x={x + 10} y={y + 18} fill="#7f8c8d" fontSize="10">{i + 1}</text>
            <text x={x + 32} y={y + 52} textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="700">{el}</text>
          </g>
        );
      })}
      {/* Legend */}
      {[
        { c: "#e74c3c", t: "Nonmetal" }, { c: "#3498db", t: "Metal" },
        { c: "#f39c12", t: "Metalloid" }, { c: "#9b59b6", t: "Noble Gas" },
      ].map((l, i) => (
        <g key={i}>
          <rect x={100 + i * 160} y="350" width="12" height="12" rx="2" fill={l.c} opacity="0.6" />
          <text x={118 + i * 160} y="361" fill="#bdc3c7" fontSize="11">{l.t}</text>
        </g>
      ))}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Molecule 3D ──────────────────────────────────────── */
export const Molecule3d: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Water Molecule (H₂O)";
  const rotation = (props.rotation as number) || 0;
  const rad = (rotation * Math.PI) / 180;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Oxygen atom (center) */}
      <circle cx="400" cy="270" r="50" fill="#e74c3c" opacity="0.8">
        <animate attributeName="r" values="50;52;50" dur="2s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="278" textAnchor="middle" fill="#ecf0f1" fontSize="24" fontWeight="700">O</text>
      {/* Hydrogen atoms orbiting */}
      {[1, -1].map((dir, i) => {
        const hx = 400 + Math.cos(rad + dir * 0.9) * 130;
        const hy = 270 + Math.sin(rad + dir * 0.9) * 80;
        return (
          <g key={i}>
            {/* Bond */}
            <line x1="400" y1="270" x2={hx} y2={hy} stroke="#546e7a" strokeWidth="8" strokeLinecap="round" />
            {/* Hydrogen */}
            <circle cx={hx} cy={hy} r="35" fill="#3498db" opacity="0.8">
              <animate attributeName="r" values="35;37;35" dur="1.8s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
            </circle>
            <text x={hx} y={hy + 7} textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="700">H</text>
          </g>
        );
      })}
      {/* Electron cloud hint */}
      <ellipse cx="400" cy="270" rx={180 + Math.sin(rad * 0.5) * 10} ry={120 + Math.cos(rad * 0.5) * 10}
        fill="none" stroke="#42a5f5" strokeWidth="1" strokeDasharray="6,6" opacity="0.15">
        <animate attributeName="stroke-dashoffset" from="12" to="0" dur="2s" repeatCount="indefinite" />
      </ellipse>
      {/* Bond angle label */}
      <text x="400" y="180" textAnchor="middle" fill="#bdc3c7" fontSize="14">104.5° bond angle</text>
      <text x="400" y="480" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Chemical Bond ────────────────────────────────────── */
export const ChemicalBond: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Chemical Bonds";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const types = (props.types as string[]) || ["Ionic", "Covalent", "Metallic"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Three bond types */}
      {[
        { x: 150, atoms: [{ el: "Na⁺", c: "#3498db" }, { el: "Cl⁻", c: "#2ecc71" }] },
        { x: 400, atoms: [{ el: "O", c: "#e74c3c" }, { el: "O", c: "#e74c3c" }] },
        { x: 650, atoms: [{ el: "Fe", c: "#bdbdbd" }, { el: "Fe", c: "#bdbdbd" }] },
      ].map((bond, i) => (
        <g key={i}>
          <text x={bond.x} y="100" textAnchor="middle" fill={i === activeIndex ? "#f9a825" : "#bdc3c7"}
            fontSize="16" fontWeight="600">{types[i]}</text>
          {/* Atom 1 */}
          <circle cx={bond.x - 45} cy="220" r={i === activeIndex ? 35 : 30}
            fill={bond.atoms[0].c} opacity={i === activeIndex ? 0.9 : 0.5}>
            {i === activeIndex && <animate attributeName="r" values="35;38;35" dur="1.2s" repeatCount="indefinite" />}
          </circle>
          <text x={bond.x - 45} y="226" textAnchor="middle" fill="#ecf0f1" fontSize="14">{bond.atoms[0].el}</text>
          {/* Bond visualization */}
          {i === 0 && (
            <g>
              <text x={bond.x} y="226" textAnchor="middle" fill="#f9a825" fontSize="20">⟷</text>
              {/* Electron transfer */}
              <circle cx={bond.x - 20} cy="215" r="4" fill="#f9a825">
                <animate attributeName="cx" values={`${bond.x - 20};${bond.x + 20}`} dur="1.5s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
          {i === 1 && (
            <g>
              <line x1={bond.x - 15} y1="215" x2={bond.x + 15} y2="215" stroke="#ecf0f1" strokeWidth="3" />
              <line x1={bond.x - 15} y1="225" x2={bond.x + 15} y2="225" stroke="#ecf0f1" strokeWidth="3" />
              {/* Shared electrons */}
              <circle cx={bond.x} cy="210" r="3" fill="#f9a825">
                <animate attributeName="cx" values={`${bond.x - 8};${bond.x + 8};${bond.x - 8}`} dur="1s" repeatCount="indefinite" />
              </circle>
              <circle cx={bond.x} cy="230" r="3" fill="#f9a825">
                <animate attributeName="cx" values={`${bond.x + 8};${bond.x - 8};${bond.x + 8}`} dur="1s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
          {i === 2 && (
            <g>
              {/* Sea of electrons */}
              {[0, 1, 2, 3, 4].map((j) => (
                <circle key={j} cx={bond.x - 30 + j * 15} cy={215 + (j % 2) * 15} r="3" fill="#f9a825" opacity="0.6">
                  <animate attributeName="cx" from={bond.x - 30 + j * 15} to={bond.x - 30 + ((j + 2) % 5) * 15} dur="1.5s" repeatCount="indefinite" />
                </circle>
              ))}
            </g>
          )}
          {/* Atom 2 */}
          <circle cx={bond.x + 45} cy="220" r={i === activeIndex ? 35 : 30}
            fill={bond.atoms[1].c} opacity={i === activeIndex ? 0.9 : 0.5}>
            {i === activeIndex && <animate attributeName="r" values="35;38;35" dur="1.2s" repeatCount="indefinite" />}
          </circle>
          <text x={bond.x + 45} y="226" textAnchor="middle" fill="#ecf0f1" fontSize="14">{bond.atoms[1].el}</text>
        </g>
      ))}
      <text x="400" y="400" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. pH Scale ─────────────────────────────────────────── */
export const PhScale: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "pH Scale";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Scale bar */}
      {Array.from({ length: 15 }, (_, i) => {
        const hue = (i / 14) * 240;
        const x = 80 + i * 44;
        return (
          <g key={i}>
            <rect x={x} y="200" width="40" height="180" rx="4"
              fill={`hsl(${hue}, 70%, ${i === activeIndex ? 55 : 40}%)`}
              opacity={i === activeIndex ? 1 : 0.6}
              stroke={i === activeIndex ? "#fff" : "none"} strokeWidth={i === activeIndex ? 2 : 0}>
              {i === activeIndex && (
                <animate attributeName="height" values="180;190;180" dur="1s" repeatCount="indefinite" />
              )}
            </rect>
            <text x={x + 20} y="405" textAnchor="middle" fill="#bdc3c7" fontSize="12" fontWeight="600">{i}</text>
          </g>
        );
      })}
      {/* Labels */}
      <text x="160" y="170" textAnchor="middle" fill="#e74c3c" fontSize="16" fontWeight="700">Acidic</text>
      <text x="400" y="170" textAnchor="middle" fill="#4caf50" fontSize="16" fontWeight="700">Neutral</text>
      <text x="640" y="170" textAnchor="middle" fill="#3498db" fontSize="16" fontWeight="700">Basic</text>
      {/* Examples */}
      <text x="120" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Battery</text>
      <text x="210" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Lemon</text>
      <text x="400" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Water</text>
      <text x="560" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Soap</text>
      <text x="660" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Bleach</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Crystal Lattice ──────────────────────────────────── */
export const CrystalLattice: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Crystal Lattice";
  const rotation = (props.rotation as number) || 0;
  const rad = rotation * Math.PI / 180;
  const offset = Math.sin(rad * 0.2) * 8;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* 3D lattice grid */}
      {Array.from({ length: 4 }, (_, r) =>
        Array.from({ length: 4 }, (_, c) => {
          const bx = 200 + c * 100 + r * 30 + offset;
          const by = 120 + r * 100 - c * 15 + offset * 0.5;
          return (
            <g key={`${r}-${c}`}>
              {/* Bonds to neighbors */}
              {c < 3 && <line x1={bx} y1={by} x2={bx + 100 + offset * 0.1} y2={by - 15} stroke="#546e7a" strokeWidth="1.5" opacity="0.4" />}
              {r < 3 && <line x1={bx} y1={by} x2={bx + 30} y2={by + 100} stroke="#546e7a" strokeWidth="1.5" opacity="0.4" />}
              {/* Atom */}
              <circle cx={bx} cy={by} r="14" fill={(r + c) % 2 === 0 ? "#3498db" : "#e74c3c"} opacity="0.7">
                <animate attributeName="r" values="14;15;14" dur={`${2 + (r + c) * 0.2}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        })
      )}
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Gas Particles ────────────────────────────────────── */
export const GasParticles: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Gas Behavior";
  const phase = (props.phase as number) || 0;
  const particleCount = 20;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Container */}
      <rect x="150" y="80" width="500" height="400" rx="12" fill="none" stroke="#546e7a" strokeWidth="3" />
      {/* Particles bouncing */}
      {Array.from({ length: particleCount }, (_, i) => {
        const hash = i * 137.508;
        const cx = 200 + (hash % 400);
        const cy = 130 + ((hash * 1.618) % 300);
        const dx = Math.sin(phase + i * 2) * 30;
        const dy = Math.cos(phase + i * 1.5) * 30;
        const colors = ["#e74c3c", "#3498db", "#2ecc71", "#f9a825", "#9b59b6"];
        return (
          <circle key={i} cx={cx + dx} cy={cy + dy} r={5 + (i % 3)}
            fill={colors[i % colors.length]} opacity="0.7">
            <animate attributeName="cx" values={`${cx};${cx + 40};${cx - 30};${cx}`} dur={`${2 + (i % 3)}s`} repeatCount="indefinite" />
            <animate attributeName="cy" values={`${cy};${cy - 35};${cy + 45};${cy}`} dur={`${2.5 + (i % 4) * 0.5}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {/* Temperature indicator */}
      <rect x="700" y="120" width="30" height="320" rx="8" fill="#1c2833" stroke="#546e7a" strokeWidth="1" />
      <rect x="704" y={440 - phase * 20 % 280} width="22" height={Math.min(280, 40 + phase * 20 % 280)} rx="6" fill="#e74c3c" opacity="0.6">
        <animate attributeName="opacity" values="0.5;0.8;0.5" dur="1s" repeatCount="indefinite" />
      </rect>
      <text x="715" y="460" textAnchor="middle" fill="#7f8c8d" fontSize="10">Temp</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Distillation Setup ───────────────────────────────── */
export const DistillationSetup: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Distillation";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Flask (boiling) */}
      <path d="M180,400 Q180,300 200,250 L200,150 L260,150 L260,250 Q280,300 280,400 Q230,420 180,400"
        fill="none" stroke="#42a5f5" strokeWidth="2" />
      <rect x="180" y="350" width="100" height="50" fill="#42a5f5" opacity="0.2" />
      {/* Bubbles */}
      {flowProgress > 0.1 && [0, 1, 2].map((i) => (
        <circle key={i} cx={210 + i * 20} cy="370" r={3 + i} fill="#42a5f5" opacity="0.3">
          <animate attributeName="cy" from="370" to="300" dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0" dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Flame under flask */}
      <path d="M230,420 Q220,400 230,380 Q240,400 230,420" fill="#ff9800" opacity="0.7">
        <animate attributeName="d" values="M230,420 Q220,400 230,380 Q240,400 230,420;M230,420 Q215,395 230,375 Q245,395 230,420;M230,420 Q220,400 230,380 Q240,400 230,420" dur="0.5s" repeatCount="indefinite" />
      </path>
      {/* Condenser tube */}
      <path d="M260,180 L400,120 L550,180" fill="none" stroke="#bdbdbd" strokeWidth="3" />
      {/* Cooling water jacket */}
      <rect x="350" y="110" width="120" height="30" rx="6" fill="none" stroke="#42a5f5" strokeWidth="1" strokeDasharray="4,3" />
      <text x="410" y="105" textAnchor="middle" fill="#42a5f5" fontSize="10">Cooling Water</text>
      {/* Vapor/liquid flow */}
      {flowProgress > 0.3 && (
        <circle r="4" fill="#90caf9" opacity="0.6">
          <animateMotion dur="3s" repeatCount="indefinite" path="M260,180 L400,120 L550,180 L580,300 L580,380" />
        </circle>
      )}
      {/* Collection flask */}
      <path d="M550,400 Q550,300 570,250 L570,180 L600,180 L600,250 Q620,300 620,400 Q585,420 550,400"
        fill="none" stroke="#42a5f5" strokeWidth="2" />
      {/* Collected liquid */}
      <rect x="555" y={400 - flowProgress * 80} width="60" height={flowProgress * 80} fill="#42a5f5" opacity="0.15" rx="0" />
      {/* Labels */}
      <text x="230" y="460" textAnchor="middle" fill="#7f8c8d" fontSize="11">Boiling Flask</text>
      <text x="585" y="430" textAnchor="middle" fill="#7f8c8d" fontSize="11">Collection</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Electrochemistry Cell ────────────────────────────── */
export const ElectrochemistryCell: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Electrochemistry";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Beaker */}
      <rect x="200" y="180" width="400" height="280" rx="8" fill="none" stroke="#546e7a" strokeWidth="3" />
      {/* Electrolyte */}
      <rect x="204" y="250" width="392" height="206" fill="#42a5f5" opacity="0.1" />
      {/* Salt bridge */}
      <rect x="380" y="200" width="40" height="200" rx="8" fill="#bdbdbd" opacity="0.2" stroke="#bdbdbd" strokeWidth="1" />
      <text x="400" y="310" textAnchor="middle" fill="#7f8c8d" fontSize="9" transform="rotate(-90,400,310)">Salt Bridge</text>
      {/* Anode */}
      <rect x="250" y="220" width="30" height="200" rx="4" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      <text x="265" y="195" textAnchor="middle" fill="#e74c3c" fontSize="14" fontWeight="700">−</text>
      <text x="265" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Anode</text>
      {/* Cathode */}
      <rect x="520" y="220" width="30" height="200" rx="4" fill="#8d6e63" stroke="#6d4c41" strokeWidth="2" />
      <text x="535" y="195" textAnchor="middle" fill="#3498db" fontSize="14" fontWeight="700">+</text>
      <text x="535" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Cathode</text>
      {/* Wire */}
      <path d="M265,220 L265,120 L535,120 L535,220" fill="none" stroke="#bdbdbd" strokeWidth="2" />
      {/* Animated electrons flowing through wire */}
      {flowProgress > 0.1 && [0, 1, 2].map((i) => (
        <circle key={i} r="4" fill="#f9a825">
          <animateMotion dur="2s" begin={`${i * 0.6}s`} repeatCount="indefinite"
            path="M265,220 L265,120 L535,120 L535,220" />
          <animate attributeName="opacity" values="0.9;0.3;0.9" dur="0.5s" repeatCount="indefinite" />
        </circle>
      ))}
      {/* Ions in solution */}
      {flowProgress > 0.2 && Array.from({ length: 6 }, (_, i) => (
        <g key={i}>
          <circle cx={240 + (i % 3) * 40} cy={300 + Math.floor(i / 3) * 40} r="5" fill="#e74c3c" opacity="0.4">
            <animate attributeName="cx" values={`${240 + (i % 3) * 40};${250 + (i % 3) * 40};${240 + (i % 3) * 40}`}
              dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={480 + (i % 3) * 30} cy={280 + Math.floor(i / 3) * 50} r="5" fill="#3498db" opacity="0.4">
            <animate attributeName="cy" values={`${280 + Math.floor(i / 3) * 50};${290 + Math.floor(i / 3) * 50};${280 + Math.floor(i / 3) * 50}`}
              dur={`${1.8 + i * 0.2}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      {/* Light bulb indicator */}
      <circle cx="400" cy="100" r="15" fill={flowProgress > 0.3 ? "#fff9c4" : "#2c3e50"} stroke="#bdbdbd" strokeWidth="2">
        {flowProgress > 0.3 && <animate attributeName="fill" values="#fff9c4;#f9a825;#fff9c4" dur="1s" repeatCount="indefinite" />}
      </circle>
      <text x="400" y="80" textAnchor="middle" fill="#7f8c8d" fontSize="10">💡</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
